// Administración · Registro de cambios (T-qtbl, handoff §12, plan §4.2):
// quién cambió qué en la configuración de la organización, incluidos los
// cambios que BitácoraPro hace por soporte (pill «BitácoraPro»). La tabla
// del prototipo —Cuándo · Quién · Sección · Cambio— sobre
// GET /organizations/me/config-audit, con los chips de filtro como
// parámetro `section` y «Cargar más» sobre el cursor del servidor.
//
// «Exportar CSV» pide /organizations/me/config-audit.csv con el mismo
// filtro, lo guarda con un Blob + <a download> (lib/csv.downloadBlob) y
// avisa cuántas filas trae. El texto «Cambio» viene listo del servidor;
// la pantalla sólo pone nombre, sección y hora.
//
// Gate: la ruta se monta detrás de canAdministerOrg en App.tsx (el
// espejo de requireOrgAdmin en core-api); aquí no hay acciones por rol.

import { useMemo, useRef, useState } from "react";
import type { ConfigAuditSection } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Panel } from "../../components/Panel";
import { QueryBoundary } from "../../components/QueryBoundary";
import { SkelTable } from "../../components/skeleton/SkelTable";
import {
  contarFilasCsv,
  filasLabel,
  fmtCuando,
  leerTextoBlob,
  nombreArchivoCsv,
  quienLabel,
  registroFiltros,
  rosterPorCorreo,
  seccionLabel,
  vistaRegistro,
} from "../../features/administracion/registro/model";
import {
  useExportarRegistro,
  useRegistroCambios,
} from "../../features/administracion/registro/queries";
import { adminWords } from "../../lib/admin/words";
import { useRecipe, useRoster } from "../../lib/api/queries";
import { downloadBlob } from "../../lib/csv";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";

// Cabeceras fijas para que el esqueleto pinte la misma fila que los datos.
const HEADERS = ["Cuándo", "Quién", "Sección", "Cambio"];

export function Registro() {
  const { data: recipe } = useRecipe();
  const roster = useRoster();
  const toast = useToast();

  const words = useMemo(() => adminWords(recipe), [recipe]);
  const assetPlural = recipe?.labels.assetPlural ?? "Activos";
  const filtros = useMemo(() => registroFiltros(words, assetPlural), [words, assetPlural]);

  const [section, setSection] = useState<ConfigAuditSection | undefined>(undefined);
  const registro = useRegistroCambios(section);
  const exportar = useExportarRegistro();

  const rosterByEmail = useMemo(() => rosterPorCorreo(roster.data?.members), [roster.data]);

  // «Cargar más» se desmonta cuando llega la última página; si tenía el
  // foco (teclado, lector de pantalla), el foco caería al <body>. Antes de
  // que desaparezca se mueve a la tabla, donde están las filas nuevas.
  const cargarMasRef = useRef<HTMLButtonElement>(null);
  const tablaRef = useRef<HTMLDivElement>(null);
  async function onCargarMas() {
    const teniaFoco = document.activeElement === cargarMasRef.current;
    const res = await registro.fetchNextPage();
    if (teniaFoco && !res.hasNextPage) tablaRef.current?.focus();
  }

  // Se cuenta antes de guardar: si el texto no se puede leer, el archivo
  // igual se descarga y el toast dice sólo «Exportado».
  async function guardarCsv(blob: Blob, filename: string | null) {
    let filas: number | null = null;
    try {
      filas = contarFilasCsv(await leerTextoBlob(blob));
    } catch {
      filas = null;
    }
    downloadBlob(blob, nombreArchivoCsv(filename));
    toast.show(
      filas === null ? "Exportado" : `Exportado · ${filasLabel(filas)}`,
      "ok",
      ADMIN_TOAST_OPTS
    );
  }

  function onExportar() {
    exportar.mutate(section, {
      onSuccess: ({ blob, filename }) => void guardarCsv(blob, filename),
      onError: () => {
        toast.show("No pudimos exportar el registro. Intenta de nuevo.", "err", ADMIN_TOAST_OPTS);
      },
    });
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow={words.module}
        title={words.sections.registro}
        sub="Quién cambió qué en la configuración. Incluye los cambios que hace el equipo BitácoraPro por soporte."
        actions={
          <button
            type="button"
            className="btn btn--secondary"
            disabled={exportar.isPending}
            onClick={onExportar}
            title="Descarga el registro filtrado"
          >
            <Icon.Download s={14} /> {exportar.isPending ? "Exportando…" : "Exportar CSV"}
          </button>
        }
      />

      <Panel
        flush
        foot={
          registro.hasNextPage && (
            <button
              ref={cargarMasRef}
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={registro.isFetchingNextPage}
              onClick={() => void onCargarMas()}
              title="Carga los cambios anteriores a los que ya ves"
            >
              {registro.isFetchingNextPage ? "Cargando…" : "Cargar más"}
            </button>
          )
        }
      >
        <div className="filters" role="group" aria-label="Filtrar por sección">
          {filtros.map((f) => {
            const activo = f.section === section;
            return (
              <button
                key={f.key}
                type="button"
                className={activo ? "filter-chip filter-chip--active" : "filter-chip"}
                aria-pressed={activo}
                onClick={() => setSection(f.section)}
                title={
                  f.section
                    ? `Muestra solo los cambios de ${f.titleNoun}`
                    : `Muestra los cambios de ${f.titleNoun}`
                }
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <QueryBoundary
          // El resultado de useInfiniteQuery trae los mismos campos de
          // estado que el de useQuery más los de paginación; vistaRegistro
          // le quita el isFetching de «Cargar más» para que no se pinte
          // «Actualizando…» encima del botón. Es la única región de datos
          // de la página, así que anuncia y toma el foco como una página
          // (kind por defecto), igual que Enlaces y Mantenciones.
          query={vistaRegistro(registro)}
          label="el registro de cambios"
          skeleton={<SkelTable rows={6} cols={HEADERS.length} headers={HEADERS} />}
          isEmpty={(data) => data.pages.every((p) => p.items.length === 0)}
          empty={
            <EmptyState
              title="Sin cambios registrados"
              description="Cuando alguien de tu equipo, o BitácoraPro por soporte, cambie la configuración, va a aparecer aquí."
            />
          }
        >
          {(data) => (
            // tabIndex -1: destino del foco cuando «Cargar más» se va con
            // la última página (ver onCargarMas); no entra en el orden de
            // tabulación.
            <div ref={tablaRef} className="dtable-scroll" tabIndex={-1}>
              <table className="dtable">
                <thead>
                  <tr>
                    {HEADERS.map((h) => (
                      <th key={h} scope="col">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.pages
                    .flatMap((p) => p.items)
                    .map((item) => (
                      <tr key={item.id}>
                        <td>
                          <time className="cell-meta" dateTime={item.createdAt}>
                            {fmtCuando(item.createdAt)}
                          </time>
                        </td>
                        <td>
                          {item.actor.kind === "support" ? (
                            <span className="role-pill role-pill--admin">BitácoraPro</span>
                          ) : (
                            <span className="user-cell__name">
                              {quienLabel(item.actor, rosterByEmail)}
                            </span>
                          )}
                        </td>
                        <td>{seccionLabel(item.section, words)}</td>
                        <td>{item.summary}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryBoundary>
      </Panel>
    </div>
  );
}

export default Registro;
